import React, { useCallback } from 'react'
import PropTypes from 'prop-types'
import {
  classNames,
  Icon,
  iconProps,
  useEventHandler
} from '@zambezi/sdk/common'
import FocusTrap from 'focus-trap-react'

/**
 * @readonly
 * @enum {import('./interfaces').ArrowDirection}
 */
const ARROW_DIRECTION = {
  LEFT: 'left',
  BOTTOM: 'bottom',
  RIGHT: 'right',
  TOP: 'top'
}

/**
 * Create a React Flyout component
 *
 * @param {import('./interfaces').FlyoutProps} props
 * @returns {JSX.Element}
 */
function Flyout (props) {
  const {
    children,
    className,
    withCloseButton,
    onClose,
    isShown,
    isError,
    arrowDirection,
    arrowPosition,
    id,
    iconProps
  } = props

  const handleDocumentKeydown = useCallback(
    event => {
      // escape
      if (isShown && event.keyCode === 27) onClose(event)
    },
    [isShown, onClose]
  )

  useEventHandler('keydown', handleDocumentKeydown)

  return (
    <FocusTrap active={isShown && withCloseButton}>
      <div
        className={classNames(
          {
            'zb-flyout': true,
            'zb-flyout-is-shown': isShown,
            'zb-flyout-is-error': isError,
            'zb-flyout-has-close-button': withCloseButton
          },
          `zb-flyout-direction-${arrowDirection}`,
          className
        )}
        aria-hidden={!isShown}
      >
        {withCloseButton && (
          <button
            className='zb-flyout-close-button zb-button-link'
            type='button'
            onClick={e => {
              e.preventDefault()
              onClose(e)
            }}
          >
            <Icon name='core-flyout-close' title='close' iconProps={iconProps} />
          </button>
        )}

        <span
          className={`zb-flyout-arrow zb-flyout-arrow-direction-${arrowDirection}`}
          style={
            arrowDirection === ARROW_DIRECTION.LEFT ||
            arrowDirection === ARROW_DIRECTION.RIGHT
              ? { top: arrowPosition }
              : { left: arrowPosition }
          }
        />

        <section className='zb-flyout-body' role='alert' id={id}>
          {children}
        </section>
      </div>
    </FocusTrap>
  )
}

Flyout.propTypes = {
  withCloseButton: PropTypes.bool,
  isShown: PropTypes.bool,
  isError: PropTypes.bool,
  onClose: PropTypes.func,
  arrowDirection: PropTypes.oneOf(Object.values(ARROW_DIRECTION)),
  arrowPosition: PropTypes.string,
  id: PropTypes.string,
  iconProps
}

Flyout.defaultProps = {
  withCloseButton: false,
  isShown: false,
  isError: false,
  arrowDirection: ARROW_DIRECTION.LEFT,
  onClose: () => {},
  arrowPosition: '50%'
}

export default Flyout
